import { AbstractControl } from '@angular/forms';

type GateFlagged = AbstractControl & { lookupGate_dynamic?: boolean };

/**
 * WI 35277 opt-in. A host that owns its form (Mode A, e.g. the SRF modal) calls this on the
 * root form to make saving wait for its dynamic lookups: the loading validators are attached
 * to the host's pre-existing controls, and a failed saved-value lookup blocks until retried.
 * Default OFF: a form that never calls it behaves exactly as before this change.
 */
export function dynamicEnableLookupGate(form: AbstractControl): void {
  (form as GateFlagged).lookupGate_dynamic = true;
}

/** True when the control's root form opted into the lookup gate. */
export function dynamicLookupGateEnabled(
  control: AbstractControl | null | undefined
): boolean {
  return (
    (control?.root as GateFlagged | undefined)?.lookupGate_dynamic === true
  );
}

type Listened = AbstractControl & { targetListeners_dynamic?: number };

/**
 * WI 35277 (code-hr, PR 4855 thread 60736): count the mounted dynamic components listening for
 * a control's itemTargetUpdate$ pushes, so a fan-out source can tell a push nobody will hear
 * (a collapsed section) from a delivered one. Returns the matching release (call it once, on
 * destroy). A missing group or control registers nothing.
 */
export function dynamicMarkTargetListener(
  group: { get?(name: string): AbstractControl | null } | null | undefined,
  config: { formControlName?: string } | null | undefined
): () => void {
  const name = config?.formControlName;
  const control = (name ? group?.get?.(name) : null) as Listened | null;
  if (!control) {
    return () => undefined;
  }
  control.targetListeners_dynamic = (control.targetListeners_dynamic ?? 0) + 1;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      control.targetListeners_dynamic = Math.max(
        0,
        (control.targetListeners_dynamic ?? 1) - 1
      );
    }
  };
}

export function dynamicHasTargetListener(
  control: AbstractControl | null | undefined
): boolean {
  return ((control as Listened | undefined)?.targetListeners_dynamic ?? 0) > 0;
}
